import { useState, useEffect } from 'react';
import { collection, query, onSnapshot, addDoc, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useUserRecords } from './useUserRecords';
import { canAddWishlistItem } from '../services/limitsService';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../config/firebase';

export interface WishlistItem {
  id: string;
  title: string;
  url?: string;
  imageUrl: string;
  price?: number;
  store?: string;
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
  isUploadedImage?: boolean;
  purchased?: boolean;
  purchasedAt?: Date;
}

export const useWishlist = () => {
  const { user } = useAuth();
  const { refreshUsage } = useUserRecords();
  const [items, setItems] = useState<WishlistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.uid) {
      setItems([]);
      setLoading(false);
      return;
    }

    const q = query(collection(db, 'users', user.uid, 'wishlist'));
    
    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const wishlistItems = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate(),
        } as WishlistItem));

        setItems(wishlistItems.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()));
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Erro ao carregar wishlist:', err);
        setError('Erro ao carregar sua wishlist');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.uid]);

  const uploadImage = async (file: File): Promise<string> => {
    if (!user?.uid) throw new Error('Usuário não autenticado');

    const storageRef = ref(storage, `wishlist/${user.uid}/${Date.now()}_${file.name}`);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  };

  const addItem = async (itemData: Omit<WishlistItem, 'id' | 'createdAt' | 'updatedAt'> & { imageFile?: File }) => {
    if (!user?.uid) throw new Error('Usuário não autenticado');

    const limitCheck = await canAddWishlistItem(user.uid);
    if (!limitCheck.allowed) {
      throw new Error(limitCheck.message);
    }

    try {
      let imageUrl = itemData.imageUrl;
      
      // Se tiver um arquivo para upload, processa ele
      if (itemData.imageFile) {
        imageUrl = await uploadImage(itemData.imageFile);
      }

      // Garantir que title existe
      if (!itemData.title || itemData.title.trim() === '') {
        throw new Error('Título é obrigatório');
      }

      // Remove campos undefined/null antes de salvar
      const newItem: any = {
        title: itemData.title.trim(),
        imageUrl,
        createdAt: new Date(),
        updatedAt: new Date(),
        isUploadedImage: !!itemData.imageFile,
      };

      // Só adicionar campos opcionais se existirem
      if (itemData.url) newItem.url = itemData.url;
      if (itemData.price) newItem.price = itemData.price;
      if (itemData.store) newItem.store = itemData.store;
      if (itemData.notes) newItem.notes = itemData.notes;

      await addDoc(collection(db, 'users', user.uid, 'wishlist'), newItem);
      await refreshUsage();
    } catch (err) {
      console.error('Erro ao adicionar item:', err);
      throw new Error('Erro ao adicionar item à wishlist');
    }
  };

  const removeItem = async (itemId: string) => {
    if (!user?.uid) throw new Error('Usuário não autenticado');

    try {
      await deleteDoc(doc(db, 'users', user.uid, 'wishlist', itemId));
      await refreshUsage();
    } catch (err) {
      console.error('Erro ao remover item:', err);
      throw new Error('Erro ao remover item da wishlist');
    }
  };

  const updateItem = async (itemId: string, updates: Partial<WishlistItem>) => {
    if (!user?.uid) throw new Error('Usuário não autenticado');

    try {
      const itemRef = doc(db, 'users', user.uid, 'wishlist', itemId);
      await updateDoc(itemRef, {
        ...updates,
        updatedAt: new Date(),
      });
    } catch (err) {
      console.error('Erro ao atualizar item:', err);
      throw new Error('Erro ao atualizar item da wishlist');
    }
  };

  return {
    items,
    loading,
    error,
    addItem,
    removeItem,
    updateItem,
    uploadImage, // Expor função de upload para uso direto
  };
};
